import { createBrowserHistory } from 'history'
import * as React from 'react'
import { Redirect, Route, Router as BrowserRouter, Switch } from 'react-router-dom'
import { Routes } from '../constants/routes'
import { useAppDispatch } from '../store'
import { setUseAppFromCRM } from '../store/slices/crm-slice'
import PrivateRouteWrapper from './private-route-wrapper'

export const history = createBrowserHistory()

export const catchChunkError = (
  fn: Function,
  retriesLeft = 3,
  interval = 500,
): Promise<{ default: React.ComponentType<any> }> => {
  return new Promise((resolve, reject) => {
    fn()
      .then(resolve)
      .catch((error: Error) => {
        // Ignore chunk cache error and retry to fetch, if cannot reload browser
        console.info(error)
        setTimeout(() => {
          if (retriesLeft === 1) {
            window.location.reload()
            return
          }
          catchChunkError(fn, retriesLeft - 1, interval).then(resolve, reject)
        }, interval)
      })
  })
}

// window['__REAPIT_MARKETPLACE_GLOBALS__'] = {
//   prpCode: 'MKT200058',
// }

const LoginPage = React.lazy(() => catchChunkError(() => import('../components/pages/login-page')))
const DocusignCallbackPage = React.lazy(() =>
  catchChunkError(() => import('../components/pages/docusign-callback-page')),
)
const HomePage = React.lazy(() => catchChunkError(() => import('../components/pages/home-page')))
const ExamplesPage = React.lazy(() => catchChunkError(() => import('../components/pages/examples-page')))
const DataPage = React.lazy(() => catchChunkError(() => import('../components/pages/data-page')))
const TemplatesPage = React.lazy(() => catchChunkError(() => import('../components/pages/templates-page')))
const TemplateEditPage = React.lazy(() => catchChunkError(() => import('../components/pages/prepare-template')))
const EmbeddedViewRedirectPage = React.lazy(() =>
  catchChunkError(() => import('../components/pages/embedded-view-redirect-page')),
)
const TemplateEditEmbeddedView = React.lazy(() =>
  catchChunkError(() => import('../components/pages/prepare-template/add-fields')),
)
const EnvelopePage = React.lazy(() => catchChunkError(() => import('../components/pages/envelope-page')))
const DesktopProcessPage = React.lazy(() => catchChunkError(() => import('../components/pages/desktop-process')))
const PostSigningPage = React.lazy(() => catchChunkError(() => import('../components/pages/post-signing')))
const PostSendingPage = React.lazy(() => catchChunkError(() => import('../components/pages/post-sending')))

const Router = () => {
  const dispatch = useAppDispatch()
  const reapitGlobalVariables = window['__REAPIT_MARKETPLACE_GLOBALS__']

  React.useEffect(() => {
    if (reapitGlobalVariables) {
      dispatch(
        setUseAppFromCRM({
          isCrm: reapitGlobalVariables ? true : false,
          variables: reapitGlobalVariables,
        }),
      )
    }
  }, [reapitGlobalVariables])

  const entryRoute =
    window['__REAPIT_MARKETPLACE_GLOBALS__'] && Object.keys(reapitGlobalVariables).length > 0
      ? Routes.DESKTOP_PROCESS
      : Routes.ENVELOPES

  return (
    <BrowserRouter history={history}>
      <React.Suspense fallback={null}>
        <Switch>
          <Route path={Routes.LOGIN} component={LoginPage} />
          <Route path="/docusign/callback" exact component={DocusignCallbackPage} />
          <Route path={Routes.EMBEDDED_VIEW_REDIRECT_PAGE} exact component={EmbeddedViewRedirectPage} />
          <PrivateRouteWrapper>
            <Switch>
              <Redirect exact from={Routes.HOME} to={entryRoute} />
              <Route path={Routes.ENVELOPES} exact component={HomePage} />
              <Route path={Routes.UPDATE_ENVELOPE} exact component={EnvelopePage} />
              <Route path={Routes.FORM} exact component={ExamplesPage} />
              <Route path={Routes.TABLE} exact component={ExamplesPage} />
              <Route path={Routes.LIST} exact component={ExamplesPage} />
              <Route path={Routes.DATA} exact component={DataPage} />
              <Route path={Routes.TEMPLATES} exact component={TemplatesPage} />
              <Route path={Routes.PREPARE_TEMPLATE} exact component={TemplateEditPage} />
              <Route path={Routes.TEMPLATE_EDIT_EMBEDDED_VIEW} exact component={TemplateEditEmbeddedView} />
              <Route path={Routes.DESKTOP_PROCESS} exact component={DesktopProcessPage} />
              <Route path={Routes.POST_SIGNING} exact component={PostSigningPage} />
              <Route path={Routes.POST_SENDING} exact component={PostSendingPage} />
            </Switch>
          </PrivateRouteWrapper>
          <Redirect to={Routes.LOGIN} />
        </Switch>
      </React.Suspense>
    </BrowserRouter>
  )
}

export default Router
